import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  WizardStepper,
  WizardStep,
  CartSummary,
  ProductsStep,
  CustomerStep,
  ReviewStep,
  ConfirmStep,
  adaptSkuToCatalogProduct,
} from './components';
import { CartLine } from './newOrderTypes';
import { useOrdersStore } from '../../../store/ordersStore';
import { useCustomersStore } from '../../../store/customersStore';
import { useSkusStore } from '../../../store/skusStore';
import styles from './SalesRepNewOrder.module.css';

const ORDER: WizardStep[] = ['products', 'customer', 'review', 'confirm'];

interface NewOrderPrefillState {
  prefillCustomerId?: string;
  prefillItems?: { sku_id: number; quantity: number }[];
}

const SalesRepNewOrder: React.FC = () => {
  // Prefill support (VP-SALES-004 — suggested-reorder "Create Order" hands
  // its edited quantities here via router state, instead of the rep
  // re-picking every SKU/quantity from scratch).
  const location = useLocation();
  const prefill = (location.state as NewOrderPrefillState | null) ?? null;
  const hasPrefillItems = !!prefill?.prefillItems?.length;

  const [step, setStep] = useState<WizardStep>(hasPrefillItems ? 'review' : 'products');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    prefill?.prefillCustomerId ?? null
  );
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const navigate = useNavigate();
  const idx = ORDER.indexOf(step);
  const isLast = idx === ORDER.length - 1;

  const createOrder = useOrdersStore((s) => s.create);
  const customers = useCustomersStore((s) => s.items);
  const fetchCustomers = useCustomersStore((s) => s.fetchList);
  const skus = useSkusStore((s) => s.items);
  const fetchSkus = useSkusStore((s) => s.fetchList);

  useEffect(() => {
    if (customers.length === 0) fetchCustomers().catch(() => undefined);
  }, [customers.length, fetchCustomers]);

  // Resolve prefill sku_ids against the real catalog (with real current
  // price/name) once it's loaded — ProductsStep normally does this fetch,
  // but a prefilled order starts on the 'review' step, skipping it.
  useEffect(() => {
    if (!hasPrefillItems || prefillApplied) return;
    if (skus.length === 0) {
      fetchSkus().catch(() => undefined);
      return;
    }
    const bySkuId = new Map(skus.map((s) => [s.id, s]));
    const lines: CartLine[] = (prefill!.prefillItems || [])
      .map((item) => {
        const sku = bySkuId.get(item.sku_id);
        return sku ? { product: adaptSkuToCatalogProduct(sku), qty: item.quantity } : null;
      })
      .filter((l): l is CartLine => l !== null);
    setCart(lines);
    setPrefillApplied(true);
  }, [hasPrefillItems, prefillApplied, skus, fetchSkus, prefill]);

  const selectedCustomer = useMemo(
    () => customers.find((c) => String(c.id) === selectedCustomerId) ?? null,
    [customers, selectedCustomerId]
  );

  const subtotal = cart.reduce((s, l) => s + l.product.rawPrice * l.qty, 0);
  const totalItems = cart.reduce((s, l) => s + l.qty, 0);

  const handleNext = async () => {
    if (!isLast) {
      setStep(ORDER[idx + 1]);
      return;
    }
    if (!selectedCustomer || cart.length === 0) {
      setSubmitError('Please select a customer and add at least one item');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await createOrder({
        customer_id: selectedCustomer.id,
        items: cart.map((l) => ({
          sku_id: l.product.skuId,
          quantity: l.qty,
          unit_price: l.product.rawPrice,
        })),
      });
      navigate('/orders');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (idx === 0) {
      navigate('/orders');
    } else {
      setStep(ORDER[idx - 1]);
    }
  };

  return (
    <div className={styles.container}>
      <WizardStepper current={step} />

      <div className={styles.body}>
        <div className={styles.main}>
          {step === 'products' && <ProductsStep cart={cart} setCart={setCart} />}
          {step === 'customer' && (
            <CustomerStep
              selectedId={selectedCustomerId}
              setSelectedId={setSelectedCustomerId}
            />
          )}
          {step === 'review' && (
            <ReviewStep
              cart={cart}
              customerName={selectedCustomer?.store_name || selectedCustomer?.full_name || ''}
              setCart={setCart}
            />
          )}
          {step === 'confirm' && (
            <ConfirmStep
              cart={cart}
              customerName={selectedCustomer?.store_name || selectedCustomer?.full_name || ''}
            />
          )}
        </div>
        {step !== 'confirm' && (
          <aside className={styles.aside}>
            <CartSummary
              itemCount={totalItems}
              subtotal={`$${subtotal.toFixed(2)}`}
              margin="—"
              total={`$${subtotal.toFixed(2)}`}
            />
          </aside>
        )}
      </div>

      {submitError && (
        <div style={{ margin: '12px 0', padding: 12, background: '#FEF2F2', color: '#B91C1C', borderRadius: 6 }}>
          {submitError}
        </div>
      )}

      <div className={styles.footer}>
        <button className={styles.backBtn} onClick={handleBack} disabled={submitting}>
          {idx === 0 ? 'Cancel' : 'Back'}
        </button>
        <button
          className={`${styles.nextBtn} ${isLast ? styles.submitBtn : ''}`}
          onClick={handleNext}
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : isLast ? '✓ Submit Order' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export const SalesRepNewOrderPage: React.FC = () => {
  return <SalesRepNewOrder />;
};

export { SalesRepNewOrder };
export default SalesRepNewOrder;
